import { ISupplierRepository } from '@domain/suppliers/ISupplierRepository';
import { Supplier, SupplierAddress } from '@domain/suppliers/models/Supplier';
import { Resource } from '@domain/base/Resource';
import { SupplierDataSource } from '../datasources/SupplierDataSource';
import { SupplierMapper } from '../mappers/SupplierMapper';
import { CreateSupplierRequestDTO, UpdateSupplierRequestDTO, LinkProductRequestDTO } from '../dto/SupplierDTO';

export class SupplierRepository implements ISupplierRepository {
  constructor(private dataSource: SupplierDataSource) { }

  async getSuppliers(): Promise<Resource<Supplier[]>> {
    try {
      const suppliers = await this.dataSource.getSuppliers();
      return {
        status: 'success',
        data: suppliers.map(SupplierMapper.toModel)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to get suppliers' }
      };
    }
  }

  async getSupplier(id: string): Promise<Resource<Supplier>> {
    try {
      const supplier = await this.dataSource.getSupplier(id);
      return {
        status: 'success',
        data: SupplierMapper.toModel(supplier)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to get supplier' }
      };
    }
  }

  async createSupplier(supplier: CreateSupplierRequestDTO): Promise<Resource<Supplier>> {
    try {
      const createdSupplier = await this.dataSource.createSupplier(supplier);
      return {
        status: 'success',
        data: SupplierMapper.toModel(createdSupplier)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to create supplier' }
      };
    }
  }

  async updateSupplier(id: string, supplier: UpdateSupplierRequestDTO): Promise<Resource<Supplier>> {
    try {
      const updatedSupplier = await this.dataSource.updateSupplier(id, supplier);
      return {
        status: 'success',
        data: SupplierMapper.toModel(updatedSupplier)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to update supplier' }
      };
    }
  }

  async deleteSupplier(id: string): Promise<Resource<void>> {
    try {
      await this.dataSource.deleteSupplier(id);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to delete supplier' }
      };
    }
  }

  async linkProducts(supplierId: string, products: LinkProductRequestDTO[]): Promise<Resource<void>> {
    try {
      await this.dataSource.linkProducts(supplierId, products);
      return {
        status: 'success',
        data: undefined
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to link product' }
      };
    }
  }

  async unlinkProducts(supplierId: string, productIds: string[]): Promise<Resource<void>> {
    try {
      await this.dataSource.unlinkProducts(supplierId, productIds);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to unlink product' }
      };
    }
  }

  async unlinkProduct(supplierId: string, productId: string): Promise<Resource<void>> {
    return this.unlinkProducts(supplierId, [productId]);
  }
} 